import React, { useContext, useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form } from "formik";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import AddIcon from "@material-ui/icons/Add";
import CancelIcon from "@material-ui/icons/Cancel";
import FlashOnIcon from "@material-ui/icons/FlashOn";

import { i18n } from "../../translate/i18n";
import { head } from "lodash";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import MessageVariablesPicker from "../MessageVariablesPicker";

import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
} from "@material-ui/core";
import ConfirmationModal from "../ConfirmationModal";

const useStyles = makeStyles((theme) => ({
    // ✅ ESTILOS PRINCIPALES
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    
    // ✅ CORREGIDO: DialogTitle dinámico según tema
    dialogTitle: {
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        padding: theme.spacing(2),
        display: 'flex',
        alignItems: 'center',
    },
    
    dialogTitleIcon: {
        marginRight: theme.spacing(1),
    },
    
    // ✅ CORREGIDO: DialogContent dinámico según tema
    dialogContent: {
        padding: theme.spacing(3),
        backgroundColor: theme.palette.background.paper,
    },
    
    formSection: {
        marginBottom: theme.spacing(3),
    },
    
    // ✅ CORREGIDO: SectionTitle dinámico según tema
    sectionTitle: {
        fontSize: '1rem',
        fontWeight: 'bold',
        color: theme.palette.text.primary,
        marginBottom: theme.spacing(1),
        display: 'flex',
        alignItems: 'center',
    },
    
    sectionIcon: {
        marginRight: theme.spacing(1),
        fontSize: '1.2rem',
    },
    
    // ✅ ESTILOS PARA CAMPOS
    fieldContainer: {
        marginBottom: theme.spacing(2),
    },
    
    shortcodeField: {
        fontFamily: 'monospace',
        fontWeight: 'bold',
    },
    
    // ✅ CORREGIDO: VariablesSection dinámico según tema
    variablesSection: {
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(2),
        borderRadius: theme.spacing(1),
        border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e9ecef'}`,
        marginTop: theme.spacing(2),
        boxShadow: theme.mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
    },
    
    // ✅ CORREGIDO: VariablesTitle dinámico según tema
    variablesTitle: {
        fontSize: '0.875rem',
        fontWeight: 'bold',
        color: theme.palette.text.secondary || theme.palette.text.primary,
        marginBottom: theme.spacing(1),
    },
    
    // ✅ CORREGIDO: VariableChip dinámico según tema
    variableChip: {
        margin: theme.spacing(0.5),
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        fontWeight: 'bold',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark || '#1565c0',
        },
    },
    
    // ✅ CORREGIDO: FileSection dinámico según tema
    fileSection: {
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(2),
        borderRadius: theme.spacing(1),
        border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e9ecef'}`,
        marginTop: theme.spacing(2),
        boxShadow: theme.mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
    },
    
    fileInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    
    // ✅ CORREGIDO: FileIcon dinámico según tema
    fileIcon: {
        color: theme.palette.primary.main,
    },
    
    deleteFileButton: {
        color: '#d32f2f',
        backgroundColor: 'rgba(211, 47, 47, 0.1)',
        borderRadius: '50%',
        padding: '4px',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
            backgroundColor: '#d32f2f',
            color: 'white',
            transform: 'scale(1.1)',
            boxShadow: '0 4px 12px rgba(211, 47, 47, 0.4)',
        },
        '&:active': {
            transform: 'scale(0.95)',
        },
    },
    
    // ✅ ESTILOS PARA BOTONES
    btnWrapper: {
        position: "relative",
    },

    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    
    // ✅ CORREGIDO: DialogActions dinámico según tema
    dialogActions: {
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e9ecef'}`,
    },
    
    // ✅ CORREGIDO: AttachButton dinámico según tema
    attachButton: {
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.main,
        backgroundColor: theme.mode === 'dark' ? 'transparent' : 'white',
        borderRadius: '8px',
        padding: '8px 16px',
        fontWeight: 'bold',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            transform: 'translateY(-2px)',
            boxShadow: `0 4px 12px ${theme.mode === 'dark' ? 'rgba(59, 130, 246, 0.4)' : 'rgba(25, 118, 210, 0.4)'}`,
        },
        '&:active': {
            transform: 'translateY(0)',
        },
    },
    
    // ✅ CORREGIDO: CancelButton dinámico según tema
    cancelButton: {
        borderColor: '#d32f2f',
        color: '#d32f2f',
        backgroundColor: theme.mode === 'dark' ? 'transparent' : 'white',
        borderRadius: '8px',
        padding: '8px 16px',
        fontWeight: 'bold',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
            backgroundColor: '#d32f2f',
            color: 'white',
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(211, 47, 47, 0.4)',
        },
        '&:active': {
            transform: 'translateY(0)',
        },
    },
    
    // ✅ CORREGIDO: SaveButton dinámico según tema
    saveButton: {
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        borderRadius: '8px',
        padding: '8px 16px',
        fontWeight: 'bold',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark || '#1565c0',
            transform: 'translateY(-2px)',
            boxShadow: `0 4px 12px ${theme.mode === 'dark' ? 'rgba(59, 130, 246, 0.4)' : 'rgba(25, 118, 210, 0.4)'}`,
        },
        '&:active': {
            transform: 'translateY(0)',
        },
    },
    
    // ✅ CORREGIDO: TextField dinámico según tema
    textField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: theme.mode === 'dark' ? '#374151' : '#f5f5f5',
            color: theme.palette.text.primary,
            '&:hover': {
                backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#eeeeee',
            },
            '&.Mui-focused': {
                backgroundColor: theme.mode === 'dark' ? '#374151' : 'white',
                boxShadow: `0 0 0 2px ${theme.mode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(25, 118, 210, 0.2)'}`,
            },
            "& fieldset": {
                borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
            },
            "&:hover fieldset": {
                borderColor: theme.palette.primary.main,
            },
            "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
            },
        },
        "& .MuiInputLabel-root": {
            color: theme.palette.text.secondary || theme.palette.text.primary,
        },
        "& .MuiInputLabel-root.Mui-focused": {
            color: theme.palette.primary.main,
        },
        "& .MuiFormHelperText-root": {
            color: theme.palette.text.secondary || theme.palette.text.primary,
        },
    },
    
    // ✅ CORREGIDO: FormControl dinámico según tema
    formControl: {
        '& .MuiOutlinedInput-root': {
            backgroundColor: theme.mode === 'dark' ? '#374151' : '#f5f5f5',
            color: theme.palette.text.primary,
            '&:hover': {
                backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#eeeeee',
            },
            "& fieldset": {
                borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
            },
            "&:hover fieldset": {
                borderColor: theme.palette.primary.main,
            },
            "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
            },
        },
        "& .MuiInputLabel-root": {
            color: theme.palette.text.secondary || theme.palette.text.primary,
            "&.Mui-focused": {
                color: theme.palette.primary.main,
            },
        },
        "& .MuiSelect-root": {
            color: theme.palette.text.primary,
        },
        "& .MuiMenuItem-root": {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            "&:hover": {
                backgroundColor: theme.mode === 'dark' ? '#374151' : '#f5f5f5',
            },
        },
    },
}));

const QuickeMessageSchema = Yup.object().shape({
    shortcode: Yup.string().required("Obligatorio"),
    //   message: Yup.string().required("Obligatorio"),
});

const QuickMessageDialog = ({ open, onClose, quickemessageId, reload }) => {
    const classes = useStyles();
    const theme = useTheme();
    const { user } = useContext(AuthContext);
    const { profile } = user;
    const messageInputRef = useRef();

    const initialState = {
        shortcode: "",
        message: "",
        geral: false,
        status: true,
    };

    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [quickemessage, setQuickemessage] = useState(initialState);
    const [attachment, setAttachment] = useState(null);
    const attachmentFile = useRef(null);

    useEffect(() => {
        try {
            (async () => {
                if (!quickemessageId) return;

                const { data } = await api.get(`/quick-messages/${quickemessageId}`);

                setQuickemessage((prevState) => {
                    const newState = { ...prevState, ...data };
                    return newState;
                });
            })();
        } catch (err) {
            toastError(err);
        }
    }, [quickemessageId, open]);

    const handleClose = () => {
        setQuickemessage(initialState);
        setAttachment(null);
        onClose();
    };

    const handleAttachmentFile = (e) => {
      
        const file = head(e.target.files);
        if (file) {
            setAttachment(file);
        }
    };

    const handleSaveQuickeMessage = async (values) => {
        // ✅ VALIDACIÓN: Asegurar que el campo message esté presente
        if (!values.message || values.message.trim() === '') {
            toast.error("El campo 'Respuesta' es obligatorio");
            return;
        }

        // ✅ Helper function para extraer nombre de archivo de URL/ruta (compatible con navegador)
        const getFileName = (filePath) => {
            if (!filePath) return null;
            // Manejar tanto rutas Unix (/) como Windows (\) y URLs
            return filePath.split('/').pop().split('\\').pop();
        };

        const quickemessageData = { 
            ...values, 
            message: values.message.trim(), // ✅ Asegurar que message esté presente y sin espacios extra
            isMedia: true, 
            mediaPath: attachment ? String(attachment.name).replace(/ /g, "_") : values.mediaPath ? getFileName(values.mediaPath).replace(/ /g, "_") : null 
        };

        try {
            if (quickemessageId) {
                await api.put(`/quick-messages/${quickemessageId}`, quickemessageData);
                if (attachment != null) {
                    const formData = new FormData();
                    formData.append("typeArch", "quickMessage");
                    formData.append("file", attachment);
                    await api.post(
                        `/quick-messages/${quickemessageId}/media-upload`,
                        formData
                    );
                }
            } else {
                const { data } = await api.post("/quick-messages", quickemessageData);
                if (attachment != null) {
                    const formData = new FormData();
                    formData.append("typeArch", "quickMessage");
                    formData.append("file", attachment);
                    await api.post(`/quick-messages/${data.id}/media-upload`, formData);
                }
            }
            toast.success(i18n.t("quickMessages.toasts.success"));
            if (typeof reload == "function") {

                reload();
            }
        } catch (err) {
            toastError(err);
        }
        handleClose();
    };

    const deleteMedia = async () => {
        if (attachment) {
            setAttachment(null);
            attachmentFile.current.value = null;
        }

        if (quickemessage.mediaPath) {
            await api.delete(`/quick-messages/${quickemessage.id}/media-upload`);
            setQuickemessage((prev) => ({
                ...prev,
                mediaPath: null,
            }));
            toast.success(i18n.t("quickMessages.toasts.deleted"));
            if (typeof reload == "function") {

                reload();
            }
        }
    };

    const handleClickMsgVar = async (msgVar, setValueFunc) => {
        const el = messageInputRef.current;
        const firstHalfText = el.value.substring(0, el.selectionStart);
        const secondHalfText = el.value.substring(el.selectionEnd);
        const newCursorPos = el.selectionStart + msgVar.length;

        setValueFunc("message", `${firstHalfText}${msgVar}${secondHalfText}`);

        await new Promise(r => setTimeout(r, 100));
        messageInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
    };

    return (
        <div className={classes.root}>
            <ConfirmationModal
                title={i18n.t("quickMessages.confirmationModal.deleteTitle")}
                open={confirmationOpen}
                onClose={() => setConfirmationOpen(false)}
                onConfirm={deleteMedia}
            >
                {i18n.t("quickMessages.confirmationModal.deleteMessage")}
            </ConfirmationModal>
            
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                scroll="paper"
                PaperProps={{
                    style: {
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: '12px',
                        boxShadow: theme.mode === 'dark' ? '0 8px 32px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.12)',
                    }
                }}
            >
                <DialogTitle className={classes.dialogTitle}>
                    <FlashOnIcon className={classes.dialogTitleIcon} />
                    {quickemessageId
                        ? `Editar Respuesta Rápida`
                        : `Agregar Respuesta Rápida`}
                </DialogTitle>
                
                <div style={{ display: "none" }}>
                    <input
                        type="file"
                        ref={attachmentFile}
                        onChange={(e) => handleAttachmentFile(e)}
                    />
                </div>
                
                <Formik
                    initialValues={quickemessage}
                    enableReinitialize={true}
                    validationSchema={QuickeMessageSchema}
                    onSubmit={(values, actions) => {
                        setTimeout(() => {
                            handleSaveQuickeMessage(values);
                            actions.setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ touched, errors, isSubmitting, setFieldValue, values }) => {
                        return (
                        <Form>
                            <DialogContent className={classes.dialogContent}>
                                {/* ✅ SECCIÓN: INFORMACIÓN BÁSICA */}
                                <div className={classes.formSection}>
                                    <Typography className={classes.sectionTitle}>
                                        <FlashOnIcon className={classes.sectionIcon} />
                                        Información Básica
                                    </Typography>
                                    
                                    <div className={classes.fieldContainer}>
                                        <TextField
                                            autoFocus
                                            label="Atajo"
                                            name="shortcode"
                                            value={values.shortcode || ""}
                                            onChange={(e) => setFieldValue('shortcode', e.target.value)}
                                            error={touched.shortcode && Boolean(errors.shortcode)}
                                            helperText={touched.shortcode && errors.shortcode}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            className={`${classes.shortcodeField} ${classes.textField}`}
                                            placeholder="Ej: 100A, 101B..."
                                        />
                                    </div>
                                    
                                    {(profile === "admin") && (
                                        <div className={classes.fieldContainer}>
                                            <FormControl variant="outlined" margin="dense" fullWidth className={classes.formControl}>
                                                <InputLabel id="geral-selection-label">
                                                    Permitir editar
                                                </InputLabel>
                                                <Select
                                                    label="Permitir editar"
                                                    placeholder="Permitir editar"
                                                    labelId="geral-selection-label"
                                                    id="geral"
                                                    name="geral"
                                                    value={values.geral || false}
                                                    onChange={(e) => setFieldValue('geral', e.target.value)}
                                                    error={touched.geral && Boolean(errors.geral)}
                                                >
                                                    <MenuItem value={true}>Activo</MenuItem>
                                                    <MenuItem value={false}>Inactivo</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </div>
                                    )}
                                </div>
                                
                                {/* ✅ SECCIÓN: MENSAJE */}
                                <div className={classes.formSection}>
                                    <Typography className={classes.sectionTitle}>
                                        <AddIcon className={classes.sectionIcon} />
                                        Contenido del Mensaje
                                    </Typography>
                                    
                                    <div className={classes.fieldContainer}>
                                        <TextField
                                            label="Respuesta"
                                            name="message"
                                            inputRef={messageInputRef}
                                            value={values.message || ""}
                                            onChange={(e) => setFieldValue('message', e.target.value)}
                                            error={touched.message && Boolean(errors.message)}
                                            helperText={touched.message && errors.message}
                                            variant="outlined"
                                            margin="dense"
                                            multiline={true}
                                            rows={6}
                                            fullWidth
                                            className={classes.textField}
                                            placeholder="Escribe aquí el contenido de la respuesta rápida..."
                                        />
                                    </div>
                                </div>
                                
                                {/* ✅ SECCIÓN: VARIABLES DISPONIBLES */}
                                <Paper className={classes.variablesSection}>
                                    <Typography className={classes.variablesTitle}>
                                        Variables disponibles
                                    </Typography>
                                    <MessageVariablesPicker
                                        disabled={isSubmitting}
                                        onClick={value => handleClickMsgVar(value, setFieldValue)}
                                    />
                                </Paper>
                                
                                {/* ✅ SECCIÓN: ARCHIVO ADJUNTO */}
                                {(quickemessage.mediaPath || attachment) && (
                                    <Paper className={classes.fileSection}>
                                        <Typography className={classes.sectionTitle}>
                                            <AttachFileIcon className={classes.sectionIcon} />
                                            Archivo Adjunto
                                        </Typography>
                                        <div className={classes.fileInfo}>
                                            <AttachFileIcon className={classes.fileIcon} />
                                            <Typography variant="body2" style={{ color: theme.palette.text.primary }}>
                                                {attachment ? attachment.name : quickemessage.mediaName}
                                            </Typography>
                                            <IconButton
                                                onClick={() => setConfirmationOpen(true)}
                                                color="secondary"
                                                size="small"
                                                className={classes.deleteFileButton}
                                                title="Eliminar archivo adjunto"
                                            >
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </div>
                                    </Paper>
                                )}
                            </DialogContent>
                            
                            <DialogActions className={classes.dialogActions}>
                                {!attachment && !quickemessage.mediaPath && (
                                    <Button
                                        onClick={() => attachmentFile.current.click()}
                                        disabled={isSubmitting}
                                        variant="outlined"
                                        startIcon={<AttachFileIcon />}
                                        className={classes.attachButton}
                                    >
                                        Adjuntar Archivo
                                    </Button>
                                )}
                                
                                <Button
                                    onClick={handleClose}
                                    disabled={isSubmitting}
                                    variant="outlined"
                                    startIcon={<CancelIcon />}
                                    className={classes.cancelButton}
                                >
                                    Cancelar
                                </Button>
                                
                                <Button
                                    type="submit"
                                    disabled={isSubmitting}
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    className={`${classes.btnWrapper} ${classes.saveButton}`}
                                >
                                    {quickemessageId ? 'Guardar Cambios' : 'Agregar Respuesta'}
                                    {isSubmitting && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </Button>
                            </DialogActions>
                        </Form>
                        );
                    }}
                </Formik>
            </Dialog>
        </div>
    );
};

export default QuickMessageDialog;